import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader, Badge, Button, Collapse, Input, InputGroup, InputGroupText,Table,Nav,NavItem,NavLink,TabContent,TabPane,FormGroup,Label,Alert } from 'reactstrap';
import {toast, ToastContainer} from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faChevronDown, 
  faChevronRight, 
  faCode, 
  faBook, 
  faCog,
  faServer,
  faInfoCircle,
  faPlay,
  faCopy,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import Select from "react-select";
import apiSpec from './apiSpec';
import projectsService from '../../services/api/projects-service';
import manageProjectService from '../../services/api/manage-project-service';
import apiExplorerService from '../../services/api/api-explorer-service';

const ApiDocumentationViewer = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedEndpoints, setExpandedEndpoints] = useState({});
  const [activeTab, setActiveTab] = useState('endpoints');
  const [selectedVersion, setSelectedVersion] = useState('v1');
  
  // 🚀 Project Selector States
  const [projectNameArray, setProjectNames] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [authToken, setAuthToken] = useState('');
  
  // 🌍 Environment Selector States
  const [selectedEnvironment, setSelectedEnvironment] = useState({ value: 'https://devapi.sortd.mobi', label: 'Production' });
  const [environmentOptions] = useState([
    { value: 'https://devapi.sortd.mobi', label: 'Production' },
    { value: 'https://devapi.sortd.dev', label: 'Staging' }
  ]);

  // 🔧 Dynamic Parameter States for each endpoint
  const [endpointParams, setEndpointParams] = useState({});
  const [endpointResponses, setEndpointResponses] = useState({});
  const [loadingEndpoints, setLoadingEndpoints] = useState({});

  // 🔁 Fetch Projects List
  useEffect(() => {
    (async () => {
      try {
        const projects = await projectsService.getPublisherProjects();
        if (projects.status) {
          const options = projects.data.map(project => ({
            value: project.id,
            label: `${project.name} (${project.slug})`
          }));
          setProjectNames(options);
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    })();
  }, []);

  // 🔁 Change Project Handler
  const changeProjectId = async (selectedOption) => {
    setProjectId(selectedOption);
    try {
      const res = await manageProjectService.getValidProjectAuthToken(selectedOption.value);
      if (res?.status) {
        setAuthToken(res.data.token);
        console.log("Auth Token:", res.data.token);
        // toast.success("Project selected and auth token retrieved!");
        
        // Update token in all endpoints
        const updatedParams = {};
        Object.keys(endpointParams).forEach(key => {
          updatedParams[key] = {
            ...endpointParams[key],
            token: res.data.token
          };
        });
        setEndpointParams(updatedParams);
      }
    } catch (error) {
      setAuthToken('');
      console.error("Error fetching auth token:", error);
      toast.error("Error fetching auth token");
    }
  };

  // 🌍 Change Environment Handler
  const changeEnvironment = (selectedOption) => {
    setSelectedEnvironment(selectedOption);
    console.log("Selected Environment:", selectedOption);
  };

  // 🔧 Parameter Value Handler
  const handleParameterChange = (endpointKey, paramName, value) => {
    setEndpointParams(prev => ({
      ...prev,
      [endpointKey]: {
        ...prev[endpointKey],
        [paramName]: value
      }
    }));
  };

  // 🔧 Initialize Parameters for Endpoint
  const initializeEndpointParams = (path, method, details) => {
    const endpointKey = `${path}#${method}`;
    
    if (!endpointParams[endpointKey]) {
      const initialValues = {};
      
      if (details.parameters) {
        details.parameters.forEach(param => {
          if (param.name === 'token') {
            initialValues[param.name] = authToken || '';
          } else if (param.name === 'page') {
            initialValues[param.name] = '1';
          } else {
            initialValues[param.name] = '';
          }
        });
      }
      
      // Handle request body parameters
      if (details.parameters) {
        const bodyParam = details.parameters.find(p => p.in === 'body');
        if (bodyParam && bodyParam.schema && bodyParam.schema.properties) {
          Object.keys(bodyParam.schema.properties).forEach(key => {
            initialValues[key] = '';
          });
        }
      }
      
      setEndpointParams(prev => ({
        ...prev,
        [endpointKey]: initialValues
      }));
    }
  };

  // 🔧 Build Dynamic URL
  const buildDynamicUrl = (path, parameters, paramValues) => {
    let dynamicPath = path;
    let queryParams = [];

    if (parameters && paramValues) {
      parameters.forEach(param => {
        const value = paramValues[param.name] || '';

        if (param.in === 'path') {
          // Replace path parameters
          dynamicPath = dynamicPath.replace(`{${param.name}}`, value || `{${param.name}}`);
        } else if (param.in === 'query' && value) {
          // Add query parameters
          queryParams.push(`${param.name}=${encodeURIComponent(value)}`);
        }
      });
    }

    // Use selected environment's value as base URL
    const baseHost = selectedEnvironment?.value || `${apiSpec.schemes[0]}://${apiSpec.host}`;
    const baseUrl = `${baseHost}${dynamicPath}`;

    return queryParams.length > 0 ? `${baseUrl}?${queryParams.join('&')}` : baseUrl;
  };

  // 🔧 Validate Required Parameters
  const validateRequiredParams = (parameters, paramValues) => {
    if (!parameters) return { valid: true, missing: [] };
    
    const missing = [];
    parameters.forEach(param => {
      if (param.required && (!paramValues[param.name] || paramValues[param.name].trim() === '')) {
        missing.push(param.name);
      }
    });
    
    return { valid: missing.length === 0, missing };
  };

  // 🔧 Execute API Call
  const executeApiCall = async (path, method, details) => {
    const endpointKey = `${path}#${method}`;
    const paramValues = endpointParams[endpointKey] || {};

    const validation = validateRequiredParams(details.parameters, paramValues);
    if (!validation.valid) {
      toast.error(`Missing required parameters: ${validation.missing.join(', ')}`);
      return;
    }

    setLoadingEndpoints(prev => ({ ...prev, [endpointKey]: true }));

    const methodUpper = method.toUpperCase();
    const url = buildDynamicUrl(path, details.parameters, paramValues);

    let bodyData = null;

    // Prepare body payload for POST/PUT/PATCH
    if (['POST', 'PUT', 'PATCH'].includes(methodUpper) && details.parameters) {
      const bodyParam = details.parameters.find(p => p.in === 'body');
      if (bodyParam && bodyParam.schema && bodyParam.schema.properties) {
        bodyData = {};
        Object.keys(bodyParam.schema.properties).forEach(key => {
          bodyData[key] = paramValues[key] || '';
        });
      }
    }

    try {
      const payload = {
        url,
        method: methodUpper,
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { Authorization: `Bearer ${authToken}` })
        },
        body: bodyData
      };

      const response = await apiExplorerService.executeApi(payload);
      console.log("API Response:", response);

      const responseData = {
        status: response?.data?.data?.status,
        statusText: response?.data?.statusText,
        data: response?.data?.data,
        url: url,
        timestamp: new Date().toISOString()
      };

      setEndpointResponses(prev => ({
        ...prev,
        [endpointKey]: responseData
      }));
      if( responseData.status) {
        toast.success('API call successful!');
      }
      else{
        toast.error(`Error: ${responseData?.data?.error?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('API Error:', error);
      const errorResponse = {
        status: error.response?.status || 'Error',
        statusText: error.response?.statusText || error.message,
        data: error.response?.data || null,
        url: url,
        timestamp: new Date().toISOString()
      };

      setEndpointResponses(prev => ({
        ...prev,
        [endpointKey]: errorResponse
      }));

      toast.error(`Error: ${error.message}`);
    } finally {
      setLoadingEndpoints(prev => ({ ...prev, [endpointKey]: false }));
    }
  };


  // Group endpoints by category
  const groupedEndpoints = {};
  Object.entries(apiSpec.paths).forEach(([path, methods]) => {
    let category = 'general';
    const pathParts = path.split('/');
    
    if (pathParts.length > 2) {
      category = pathParts[2];
    }
    
    if (!groupedEndpoints[category]) {
      groupedEndpoints[category] = {};
    }
    groupedEndpoints[category][path] = methods;
  });

  // Filter endpoints based on search term
  const filteredEndpoints = {};
  Object.entries(groupedEndpoints).forEach(([category, endpoints]) => {
    const filtered = Object.entries(endpoints).filter(([path]) => 
      path.toLowerCase().includes(searchTerm.toLowerCase())
    );
    if (filtered.length > 0) {
      filteredEndpoints[category] = Object.fromEntries(filtered);
    }
  });

  const toggleEndpoint = (path) => {
    setExpandedEndpoints(prev => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  const getMethodColor = (method) => {
    const colors = {
      get: 'success',
      post: 'primary',
      put: 'warning',
      delete: 'danger',
      patch: 'info'
    };
    return colors[method] || 'secondary';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const renderParameterInputs = (path, method, details) => {
    const endpointKey = `${path}#${method}`;
    const paramValues = endpointParams[endpointKey] || {};

    // Initialize params if not exists
    if (!endpointParams[endpointKey]) {
      initializeEndpointParams(path, method, details);
      return null;
    }

    if (!details.parameters || details.parameters.length === 0) return null;

    // Group parameters by type
    const pathParams = details.parameters.filter(p => p.in === 'path');
    const queryParams = details.parameters.filter(p => p.in === 'query');
    const bodyParam = details.parameters.find(p => p.in === 'body');

    return (
      <div className="mt-3">
        <h6><FontAwesomeIcon icon={faCog} className="me-2" />Parameters</h6>

        {/* Path Parameters Section */}
        {pathParams.length > 0 && (
          <div className="mb-4">
            <h6 className="text-muted mb-2">
              <Badge color="warning" className="me-2">Path Parameters</Badge>
              Required URL path variables
            </h6>
            <Row>
              {pathParams.map((param, idx) => (
                <Col md={6} key={idx} className="mb-2">
                  <FormGroup>
                    <Label>
                      {param.required && <span className="text-danger me-1">*</span>}
                      {param.name}
                    </Label>
                    <Input
                      type={param.type === 'integer' ? 'number' : 'text'}
                      value={paramValues[param.name] || ''}
                      onChange={(e) => handleParameterChange(endpointKey, param.name, e.target.value)}
                      placeholder={`Enter ${param.name}`}
                      size="sm"
                    />
                    {param.description && (
                      <small className="text-muted">{param.description}</small>
                    )}
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Query Parameters Section */}
        {queryParams.length > 0 && (
          <div className="mb-4">
            <h6 className="text-muted mb-2">
              <Badge color="info" className="me-2">Query Parameters</Badge>
              Optional URL query string parameters
            </h6>
            <Row>
              {queryParams.map((param, idx) => (
                <Col md={6} key={idx} className="mb-2">
                  <FormGroup>
                    <Label>
                      {param.required && <span className="text-danger me-1">*</span>}
                      {param.name}
                    </Label>
                    <Input
                      type={param.type === 'integer' ? 'number' : 'text'}
                      value={paramValues[param.name] || ''}
                      onChange={(e) => handleParameterChange(endpointKey, param.name, e.target.value)}
                      placeholder={`Enter ${param.name}`}
                      size="sm"
                    />
                    {param.description && (
                      <small className="text-muted">{param.description}</small>
                    )}
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </div>
        )}

        {/* Request Body Parameters Section */}
        {bodyParam && bodyParam.schema && bodyParam.schema.properties && (
          <div className="mb-4">
            <h6 className="text-muted mb-2">
              <Badge color="primary" className="me-2">Request Body</Badge>
              JSON payload parameters
            </h6>
            <Row>
              {Object.entries(bodyParam.schema.properties).map(([key, prop]) => (
                <Col md={6} key={key} className="mb-2">
                  <FormGroup>
                    <Label>
                      {bodyParam.schema.required && bodyParam.schema.required.includes(key) &&
                        <span className="text-danger me-1">*</span>
                      }
                      {key}
                    </Label>
                    <Input
                      type="text"
                      value={paramValues[key] || ''}
                      onChange={(e) => handleParameterChange(endpointKey, key, e.target.value)}
                      placeholder={`Enter ${key}`}
                      size="sm"
                    />
                    {prop.description && (
                      <small className="text-muted">{prop.description}</small>
                    )}
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </div>
    );
  };

  const renderResponse = (path, method) => {
    const endpointKey = `${path}#${method}`;
    const response = endpointResponses[endpointKey];
    
    if (!response) return null;
    
    return (
      <div className="mt-3">
        <h6>Response:</h6>
        <div className="mb-2">
          <Badge color={response.status ? 'success' : 'danger'}>
            {response.status ? 200 : ''} {response.status ? 'OK' : 'Error'}
          </Badge>
          <small className="text-muted ms-2">{response.timestamp}</small>
        </div>
        <div className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          <pre>{JSON.stringify(response.data, null, 2)}</pre>
        </div>
      </div>
    );
  };

  const renderEndpoint = (path, methods) => {
    const isExpanded = expandedEndpoints[path];
    
    return (
      <Card key={path} className="mb-3 shadow-sm">
        <CardHeader className="d-flex justify-content-between align-items-center">
          <div 
            className="cursor-pointer d-flex align-items-center flex-grow-1"
            onClick={() => toggleEndpoint(path)}
          >
            <FontAwesomeIcon 
              icon={isExpanded ? faChevronDown : faChevronRight} 
              className="me-2"
            />
            <div>
              <div className="d-flex align-items-center mb-1">
                {Object.keys(methods).map(method => (
                  <Badge key={method} color={getMethodColor(method)} className="me-2">
                    {method.toUpperCase()}
                  </Badge>
                ))}
                <code className="ms-2">{path}</code>
              </div>
            </div>
          </div>
          
          {/* Try It Out Button - Now outside collapse */}
          <div className="d-flex gap-2">
            {Object.entries(methods).map(([method, details]) => {
              const endpointKey = `${path}#${method}`;
              const isLoading = loadingEndpoints[endpointKey];
              
              // Initialize params when button is rendered
              if (!endpointParams[endpointKey]) {
                initializeEndpointParams(path, method, details);
              }
              
              return (
                <Button
                  key={method}
                  color="primary"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent header click
                    executeApiCall(path, method, details);
                  }}
                  disabled={isLoading}
                  className="flex-shrink-0"
                >
                  {isLoading ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin className="me-1" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPlay} className="me-1" />
                      Try {method.toUpperCase()}
                    </>
                  )}
                </Button>
              );
            })}
          </div>
        </CardHeader>
        
        <Collapse isOpen={isExpanded}>
          <CardBody>
            {Object.entries(methods).map(([method, details]) => {
              const endpointKey = `${path}#${method}`;
              const paramValues = endpointParams[endpointKey] || {};
              
              return (
                <div key={method} className="mb-4">
                  <div className="mb-3">
                    <strong>Dynamic URL:</strong>
                    <div className="bg-light p-2 rounded mt-1 d-flex justify-content-between align-items-center">
                      <code>{buildDynamicUrl(path, details.parameters, paramValues)}</code>
                      <Button 
                        color="outline-secondary" 
                        size="sm"
                        onClick={() => copyToClipboard(buildDynamicUrl(path, details.parameters, paramValues))}
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </Button>
                    </div>
                  </div>
                  
                  {renderParameterInputs(path, method, details)}
                  
                  {renderResponse(path, method)}
                  
                  {/* <div className="mt-3">
                    <h6>Available Responses</h6>
                    <Table size="sm" responsive>
                      <thead>
                        <tr>
                          <th>Status</th>
                          <th>Type</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(details.responses).map(([status, response]) => (
                          <tr key={status}>
                            <td>
                              <Badge color={status === '200' ? 'success' : status === 'default' ? 'secondary' : 'warning'}>
                                {status}
                              </Badge>
                            </td>
                            <td>{response.description || 'No description'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div> */}
                </div>
              );
            })}
          </CardBody>
        </Collapse>
      </Card>
    );
  };

  const renderOverview = () => (
    <Card className="shadow-sm">
        {!authToken && (
          <Alert color="info" className="mb-2">
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            Please select a project to get an auth token. Many endpoints require authentication.
          </Alert>
        )}
      <CardHeader>
        <h5><FontAwesomeIcon icon={faInfoCircle} className="me-2" />API Overview</h5>
      </CardHeader>
      <CardBody>
        <Row>
          <Col md={6}>
            <h6>API Information</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td><strong>Title:</strong></td>
                  <td>{apiSpec.info.title}</td>
                </tr>
                <tr>
                  <td><strong>Version:</strong></td>
                  <td><Badge color="primary">{apiSpec.info.version}</Badge></td>
                </tr>
                <tr>
                  <td><strong>Host:</strong></td>
                  <td><code>{selectedEnvironment.value || `${apiSpec.schemes[0]}://${apiSpec.host}`}</code></td>
                </tr>
                <tr>
                  <td><strong>Base Path:</strong></td>
                  <td><code>{apiSpec.basePath}</code></td>
                </tr>
              </tbody>
            </Table>
          </Col>
          <Col md={6}>
            <h6>Statistics</h6>
            <Table borderless size="sm">
              <tbody>
                <tr>
                  <td><strong>Total Endpoints:</strong></td>
                  <td><Badge color="info">{Object.keys(apiSpec.paths).length}</Badge></td>
                </tr>
                <tr>
                  <td><strong>Categories:</strong></td>
                  <td><Badge color="success">{Object.keys(groupedEndpoints).length}</Badge></td>
                </tr>
                <tr>
                  <td><strong>Auth Token:</strong></td>
                  <td>
                    {authToken ? (
                      <div className="d-flex align-items-center">
                        <Badge color="success">Active</Badge>
                        <Button
                          color="outline-secondary"
                          size="sm"
                          className="ms-2"
                          onClick={() => copyToClipboard(authToken)}
                        >
                          <FontAwesomeIcon icon={faCopy} />
                        </Button>
                      </div>
                    ) : (
                      <Badge color="warning">Not Set</Badge>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );

  return (
    <>
      <div className="content api-documentation-viewer" style={{ minHeight: '100vh', backgroundColor: '#f8f9fa' }}>
        <div className="text-white py-4" style={{ backgroundColor: '#66615b' }}>
          <Container>
            <Row>
              <Col>
                <h2 className="mb-2">
                  <FontAwesomeIcon icon={faBook} className="me-3" />
                  API Explorer
                </h2>
                <p className="mb-0">DevAPI Documentation</p>
              </Col>
            </Row>
          </Container>
        </div>

        <Container className="py-4">
          <Row className="mb-4">
            <Col md={6}>
              <h6 className="mb-2">Select Environment</h6>
              <Select
                className="react-select primary"
                classNamePrefix="react-select"
                placeholder="Select Environment..."
                name="environment"
                id="environment"
                options={environmentOptions}
                value={selectedEnvironment}
                onChange={changeEnvironment}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>
            <Col md={6}>
              <h6 className="mb-2">Select Project</h6>
              <Select
                className="react-select primary"
                classNamePrefix="react-select"
                placeholder="Select Project..."
                name="project_id"
                id="project_id"
                options={projectNameArray}
                value={projectId}
                onChange={changeProjectId}
                menuPortalTarget={document.body}
                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
              />
            </Col>
          </Row>

          <Row>
            <Col md={3}>
              <div className="sticky-top" style={{ top: '20px' }}>
                <Card className="shadow-sm px-3 py-2">
                  <CardHeader>
                    <h5 className="mb-2"><strong>Navigation</strong></h5>
                  </CardHeader>
                  <CardBody className="p-0 nav-scroll-container">
                      <Nav vertical pills className="py-2 px-2">
                      <NavItem>
                        <NavLink 
                          className={activeTab === 'overview' ? 'active' : ''}
                          onClick={() => setActiveTab('overview')}
                        >
                          <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                          Overview
                        </NavLink>
                      </NavItem>
                      <NavItem>
                        <NavLink 
                          className={activeTab === 'endpoints' ? 'active' : ''}
                          onClick={() => setActiveTab('endpoints')}
                        >
                          <FontAwesomeIcon icon={faCode} className="me-2" />
                          All Endpoints
                        </NavLink>
                      </NavItem>
                      {Object.keys(groupedEndpoints).map(category => (
                        <NavItem key={category}>
                          <NavLink 
                            className={activeTab === category ? 'active' : ''}
                            onClick={() => setActiveTab(category)}
                          >
                            <FontAwesomeIcon icon={faServer} className="me-2" />
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                            <Badge color="light" className="ms-2">
                              {Object.keys(groupedEndpoints[category]).length}
                            </Badge>
                          </NavLink>
                        </NavItem>
                      ))}
                    </Nav>
                  </CardBody>
                </Card>
              </div>
            </Col>

            <Col md={9}>
              {activeTab === 'endpoints' && (
                <div className="mb-4">
                  <InputGroup>
                    <InputGroupText>
                      <FontAwesomeIcon icon={faSearch} />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="Search endpoints..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </InputGroup>
                </div>
              )}

              <TabContent activeTab={activeTab}>
                <TabPane tabId="overview">
                  {renderOverview()}
                </TabPane>
                
                <TabPane tabId="endpoints">
                  {Object.entries(filteredEndpoints).map(([category, endpoints]) => (
                    <div key={category} className="mb-4">
                      <h4 className="text-capitalize mb-3 border-bottom pb-2">
                        <FontAwesomeIcon icon={faServer} className="me-2" />
                        {category}
                        <Badge color="secondary" className="ms-2">
                          {Object.keys(endpoints).length}
                        </Badge>
                      </h4>
                      {Object.entries(endpoints).map(([path, methods]) => 
                        renderEndpoint(path, methods)
                      )}
                    </div>
                  ))}
                </TabPane>
                
                {Object.keys(groupedEndpoints).map(category => (
                  <TabPane key={category} tabId={category}>
                    <h4 className="text-capitalize mb-3 border-bottom pb-2">
                      <FontAwesomeIcon icon={faServer} className="me-2" />
                      {category}
                      <Badge color="secondary" className="ms-2">
                        {Object.keys(groupedEndpoints[category]).length}
                      </Badge>
                    </h4>
                    {Object.entries(groupedEndpoints[category]).map(([path, methods]) => 
                      renderEndpoint(path, methods)
                    )}
                  </TabPane>
                ))}
              </TabContent>
            </Col>
          </Row>
        </Container>

        <style jsx>{`
          .cursor-pointer {
            cursor: pointer;
          }
          
          .api-documentation-viewer .card {
            transition: all 0.3s ease;
          }
          
          .api-documentation-viewer .card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
          }
          
          .sticky-top {
            position: sticky;
            top: 20px;
            z-index: 100;
          }
          
          pre {
            font-size: 0.875rem;
            max-height: 300px;
            overflow-y: auto;
          }
          
          .nav-pills .nav-link {
            border-radius: 0.375rem;
            margin-bottom: 0.25rem;
          }
          
          .nav-pills .nav-link.active {
            background-color: #007bff;
          }

          .nav-scroll-container {
            max-height: 75vh;
            overflow-y: auto;
          } 

          /* Optional: Smooth, custom scrollbar for better UX */
          .nav-scroll-container::-webkit-scrollbar {
            width: 6px;
          }

          .nav-scroll-container::-webkit-scrollbar-track {
            background: transparent;
          }

          .nav-scroll-container::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 10px;
          }
          
          @media (max-width: 768px) {
            .sticky-top {
              position: relative;
              top: auto;
            }
          }
        `}</style>
      </div>
      <ToastContainer />
    </>
  );
};

export default ApiDocumentationViewer;